import { unwrap, type Api } from "../api/index.js";
import type { RecoveryActionName } from "../recovery/index.js";
import { detailAction } from "../vault/item_detail.js";

/**
 * Get a quote (W-10), headless: the pricing and quotation calls, query keys,
 * the `?term=` plan, the auto-applied discount badge, the price-summary rows
 * and the quote error-code mapping. The server picks the underwriter and
 * owns every amount; nothing here computes a premium, a daily cost, a
 * discount, a total or a renewal — it only selects the server's numbers.
 */

// ---------------------------------------------------------------- data

export const PLAN_TERMS = ["1_month", "3_months", "6_months", "12_months"] as const;
export type PlanTerm = (typeof PLAN_TERMS)[number];

export const DEFAULT_PLAN_TERM: PlanTerm = "12_months";

export interface QuoteDiscount {
  code: string;
  amount: number;
  type: "PERCENTAGE" | "FIXED";
  netAmount: number;
  autoApply: boolean;
}

/** The `Quotation` fields the plan screen reads. */
export interface Quote {
  /** Absent on `POST /pricing` quotes (not stored); set once saved. */
  _id?: string;
  prospectId?: string;
  plan: PlanTerm;
  underwriter: string;
  startDate: string;
  endDate: string;
  appliedDiscounts?: QuoteDiscount[];
  renewal?: { date: string; amount: number };
  pricing: {
    itemValue?: number;
    currency?: string;
    policyDays?: number;
    payment: { invoiceTotal: number };
    daily?: { dailyCharge?: number };
    tax?: { gstRate?: number; gstAmount?: number };
    discounts?: { totalDiscountAmount?: number };
  };
}

/** A quote the server stored (`POST /quotations`, `GET /quotations/:id`). */
export type StoredQuote = Quote & { _id: string };

export const quoteQueryKeys = {
  all: ["quote"] as const,
  /** POST /pricing for an item (never cached across items). */
  pricing: (itemId: string) => ["quote", "pricing", itemId] as const,
  quotation: (id: string) => ["quote", "quotation", id] as const,
};

export interface QuoteApi {
  /** POST /pricing {prospectId}: the server picks the underwriter; one quote per plan. */
  price(itemId: string): Promise<Quote[]>;
  /** POST /quotations {prospectId, plan}: the server prices and stores the quote. */
  create(input: { itemId: string; plan: PlanTerm }): Promise<StoredQuote>;
  /** GET /quotations/:id (404 when not the caller's). */
  get(id: string): Promise<StoredQuote>;
}

export function createQuoteApi(api: Api): QuoteApi {
  const c = api.shared;
  return {
    async price(itemId) {
      const r = await unwrap(c.POST("/api/sg/pricing/", { body: { prospectId: itemId } }));
      return r.data.quotations as unknown as Quote[];
    },
    async create({ itemId, plan }) {
      const r = await unwrap(c.POST("/api/sg/quotations/", { body: { prospectId: itemId, plan } }));
      return r.data as unknown as StoredQuote;
    },
    async get(id) {
      const r = await unwrap(c.GET("/api/sg/quotations/{id}", { params: { path: { id } } }));
      return r.data as unknown as StoredQuote;
    },
  };
}

// ---------------------------------------------------------------- plan / URL

export function isPlanTerm(v: unknown): v is PlanTerm {
  return typeof v === "string" && (PLAN_TERMS as readonly string[]).includes(v);
}

/** `choosePlan.term.<plan>`, e.g. "12 months". */
export function planTermKey(plan: PlanTerm | string): string {
  return isPlanTerm(plan) ? `choosePlan.term.${plan}` : "choosePlan.term.unknown";
}

/**
 * The selected plan from `?term=`: the value when the server offered it, else
 * 12 months when offered, else the first quote. Undefined when there is none.
 */
export function selectedPlan(term: string | null | undefined, quotes: readonly Pick<Quote, "plan">[]): PlanTerm | undefined {
  if (isPlanTerm(term) && quotes.some((q) => q.plan === term)) return term;
  if (quotes.some((q) => q.plan === DEFAULT_PLAN_TERM)) return DEFAULT_PLAN_TERM;
  return quotes[0]?.plan;
}

/** `/vault/:id/plan`, with `?term=` unless it is the default. */
export function planRoute(itemId: string, term?: PlanTerm): string {
  const base = `/vault/${encodeURIComponent(itemId)}/plan`;
  return term && term !== DEFAULT_PLAN_TERM ? `${base}?term=${term}` : base;
}

/** W-11's Pay route for a stored quotation. */
export function payRoute(itemId: string, quotationId: string): string {
  return `/vault/${encodeURIComponent(itemId)}/pay/${encodeURIComponent(quotationId)}`;
}

// ---------------------------------------------------------------- view models

export type DiscountBadge =
  /**
   * "{n}% OFF". `percent` is in percent units for `format.percent` (the
   * discount's configured fraction 0.1 → 10): a unit change of the config
   * value, not a computed discount.
   */
  | { kind: "percent"; percent: number; code: string }
  /** "{amount} OFF" in the quote's currency. */
  | { kind: "amount"; amount: number; code: string };

/**
 * The badge for an auto-applied discount (partner link, campaign) the server
 * applied to this quote. Pricing is single-best, so there is at most one.
 * A manual code never shows here (codes live on Pay, W-12).
 */
export function discountBadge(quote: Pick<Quote, "appliedDiscounts">): DiscountBadge | null {
  const d = (quote.appliedDiscounts ?? []).find((x) => x.autoApply && x.amount > 0);
  if (!d) return null;
  return d.type === "PERCENTAGE"
    ? { kind: "percent", percent: Math.round(d.amount * 10_000) / 100, code: d.code }
    : { kind: "amount", amount: d.amount, code: d.code };
}

/** The server's daily cost, or undefined to hide the line. */
export function dailyCost(quote: Pick<Quote, "pricing">): number | undefined {
  const v = quote.pricing.daily?.dailyCharge;
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
}

export type SummaryRow =
  | { kind: "discount"; code: string; amount: number }
  | { kind: "gst"; amount: number }
  | { kind: "total"; amount: number };

/**
 * PriceSummary rows, each one server amount as sent: every applied discount
 * (`netAmount`), the GST included (when above 0) and the total
 * (`invoiceTotal`). No row is derived from another.
 */
export function summaryRows(quote: Pick<Quote, "pricing" | "appliedDiscounts">): SummaryRow[] {
  const rows: SummaryRow[] = [];
  for (const d of quote.appliedDiscounts ?? []) {
    if (d.netAmount > 0) rows.push({ kind: "discount", code: d.code, amount: d.netAmount });
  }
  const gst = quote.pricing.tax?.gstAmount;
  if (typeof gst === "number" && gst > 0) rows.push({ kind: "gst", amount: gst });
  rows.push({ kind: "total", amount: quote.pricing.payment.invoiceTotal });
  return rows;
}

/** The expected renewal line, when the server sent one. */
export function expectedRenewal(quote: Pick<Quote, "renewal">): { date: string; amount: number } | undefined {
  const r = quote.renewal;
  return r && typeof r.amount === "number" && r.date ? r : undefined;
}

/** Coverage lines for an item: `only` limits a line to those classifications, `except` drops it for them. */
export function coverageLinesFor<L extends { only?: string[]; except?: string[] }>(
  lines: readonly L[] | undefined,
  classification: string | undefined,
): L[] {
  const c = classification ?? "";
  return (lines ?? []).filter((l) => (!l.only || l.only.includes(c)) && !(l.except ?? []).includes(c));
}

// ---------------------------------------------------------------- errors

export type QuoteErrorOutcome =
  /** Show a RecoveryView with this action. */
  | { kind: "recovery"; action: RecoveryActionName }
  /** A rejected discount code: shown as the code field's error (W-12). */
  | { kind: "field"; field: "discountCode" | "termsAcceptedAt" };

/**
 * Quote error codes → what the screen shows. `no_pricing_for_item` and
 * `invalid_plan` can't be fixed by the customer: contact support.
 * `discount_*` are field errors (W-12 Koupon). Anything else is
 * undefined: use the server's RecoveryAction.
 */
export function quoteErrorOutcome(errorCode: string | undefined): QuoteErrorOutcome | undefined {
  if (errorCode === "no_pricing_for_item" || errorCode === "invalid_plan") {
    return { kind: "recovery", action: "contact_support" };
  }
  if (errorCode === "discount_terms_required") return { kind: "field", field: "termsAcceptedAt" };
  if (errorCode?.startsWith("discount_")) return { kind: "field", field: "discountCode" };
  return undefined;
}

/**
 * The entry gate: Choose plan is reachable only where the item detail offers
 * it (uninsured, expired, approved_underwriter_review) and not archived.
 */
export function canChoosePlan(item: { status?: string; archived?: boolean; policyId?: string }): boolean {
  return !item.archived && detailAction(item).kind === "choose_plan";
}
